import {
  DescriptiveScriptBase,
  SelectionalScriptBase,
  ConversationalScriptBase,
} from '../data/Scripts.schema';
import type { Dialog, DialogScene } from '../data/Dialogs.schema';
import { species, type PetSpeciesID } from '../pet/pet.ruleset';
import { isKey } from '../utils/types';

export type { Dialog, DialogScene };
export type DialogID = `${PetSpeciesID}_${number}`;
export type ScriptID = `${PetSpeciesID}_${number}_${number}`;

const WANT_TO_ADD_FRIEND_PREFIX = 'want_to_add_friend_';

/**
 * A dialog id split into its `dialogCounts` path: the species and the unpadded dialog number
 * (`alpaca_001` is `['alpaca', '1']`). The client builds one dialog per species outside
 * `Dialogs.json`, `want_to_add_friend_dog`, which has no number and is `['dog', 'want_to_add_friend']`.
 * Throws on anything else: the id comes from the client and is used as an object key, so it
 * must never be something like `__proto__`.
 */
export function splitDialogId(
  id: string,
): [species: PetSpeciesID, key: string] {
  if (id.startsWith(WANT_TO_ADD_FRIEND_PREFIX)) {
    const speciesId = id.slice(WANT_TO_ADD_FRIEND_PREFIX.length);
    if (!(species as string[]).includes(speciesId)) {
      throw new Error(`Invalid dialog id: ${id}`);
    }
    return [speciesId as PetSpeciesID, 'want_to_add_friend'];
  }

  const separator = id.lastIndexOf('_');
  const speciesId = id.slice(0, separator);
  const number = Number(id.slice(separator + 1));
  if (!(species as string[]).includes(speciesId) || !Number.isInteger(number)) {
    throw new Error(`Invalid dialog id: ${id}`);
  }
  return [speciesId as PetSpeciesID, String(number)];
}

export const dialogScenes: DialogScene[] = [
  'normal_chat',
  'friend_chat',
  'cured',
  'not_cured',
  'care_action_blocker',
  'initial_greetings',
  'friend_greetings',
  'friend_welcome',
  'friend_appreciation',
  'friend_goodby',
  'friend_farewell',
  'friend_kickedout',
  'had_visitor',
  'duo_inviter_invite',
  'duo_invitee_invited',
  'duo_inviter_home',
  'duo_invitee_home',
  'duo_inviter_notified',
  'duo_invitee_answer',
  'duo_invitee_accept',
  'duo_invitee_refuse',
  'duo_invitee_message_intro',
  'duo_invitee_message',
  'duo_inviter_refused',
  'daycare_check_out',
  'daycare_check_in',
  'normal_play_match',
  'friend_petrevived',
  'friend_petdead',
  'tutorial_recover',
];

export type DialogData = Dialog & { id: DialogID; script_id: ScriptID };

// ----------------------------------
// Scripts
// ----------------------------------
// Make the ScriptIDs strict for typesafety
type StrictID = {
  id: ScriptID;
  next_script_id?: ScriptID;
  next_script_id_1?: ScriptID;
  next_script_id_2?: ScriptID;
  next_script_id_3?: ScriptID;
};

// One phrase of dialog
export type ConversationalScript = ConversationalScriptBase & StrictID;

// Ask player to choose options
export type SelectionalScript = SelectionalScriptBase & StrictID;

// Ask player to enter answer
export type DescriptiveScript = DescriptiveScriptBase & StrictID;

export type Script =
  | ConversationalScript
  | SelectionalScript
  | DescriptiveScript;

export const scriptIsConversational = (
  script: Script,
): script is ConversationalScript => script.type === 'conversational';
export const scriptIsSelectional = (
  script: Script,
): script is SelectionalScript => script.type === 'selectional';
export const scriptIsDescriptive = (
  script: Script,
): script is DescriptiveScript => script.type === 'descriptive';

export function getNextScriptId(
  script: Script,
  answer?: number,
): ScriptID | undefined {
  if (script.next_script_id) {
    return script.next_script_id;
  }

  const nextScriptKey = `next_script_id_${answer as any}` as const;
  if (isKey(nextScriptKey, script)) {
    return script[nextScriptKey];
  }

  return undefined;
}
